import {useQuery} from '@tanstack/react-query';
import type {CompanyConfiguration} from 'src/core/data/api/CompanyConfiguration';
import CompanyService from 'src/services/company-service';

export const companyKeys = {
  all: ['company'] as const,
  configurations: () => [...companyKeys.all, 'configurations'] as const,
  list: () => [...companyKeys.configurations(), 'list'] as const,
  resolve: () => [...companyKeys.configurations(), 'resolve'] as const,
  byHostname: (hostname: string) => [...companyKeys.resolve(), hostname] as const,
  mine: () => [...companyKeys.configurations(), 'mine'] as const,
};

export const useCompanyConfigurations = () => {
  const companyService = new CompanyService();

  return useQuery<CompanyConfiguration[]>({
    queryKey: companyKeys.list(),
    queryFn: () => companyService.listCompanies(),
  });
};

export const useCompanyConfiguration = (hostname: string) => {
  const companyService = new CompanyService();

  return useQuery({
    queryKey: companyKeys.byHostname(hostname),
    queryFn: () => companyService.getByHostname(hostname),
    enabled: !!hostname,
    retry: (_failureCount, error) => {
      const status = (error as { status?: number })?.status;
      if (status === 404) return false;
      return _failureCount < 3;
    },
  });
};

/**
 * Configuração da central de quem está logado -- domínio/subdomínio só vale antes do login (tela
 * de login, useCompanyConfiguration acima); depois de autenticado, a marca passa a ser a da
 * central que o usuário está vinculado, não mais a do host que serviu a página.
 */
export const useMyCompanyConfiguration = (enabled: boolean) => {
  const companyService = new CompanyService();

  return useQuery({
    queryKey: companyKeys.mine(),
    queryFn: () => companyService.getMine(),
    enabled,
    retry: (_failureCount, error) => {
      const status = (error as { status?: number })?.status;
      if (status === 404) return false;
      return _failureCount < 3;
    },
  });
};
